import React, { useState, useMemo, useEffect } from 'react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import fetchApi from '../utils/api.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';

function InvoiceTypeList({ onAdd, onEdit, onDelete }) {
    const { hasPermission } = usePermissionContext();
    const [rows, setRows] = useState([]);
    const [totalEntries, setTotalEntries] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const user = JSON.parse(sessionStorage.getItem('user'));
    const isSuperAdmin = user?.role === 'SUPER_ADMIN';

    const fetchInvoiceTypes = async (searchQuery = '', currentPage = 1, limit = 10) => {
        try {
            setLoading(true);
            const result = await fetchApi(`/invoice-type?search=${searchQuery}&page=${currentPage}&limit=${limit}`);

            if (result.invoiceTypes && Array.isArray(result.invoiceTypes)) {
                setRows(result.invoiceTypes);
                setTotalEntries(result.total || 0);
                setTotalPages(result.pages || 1);
            } else if (Array.isArray(result)) {
                setRows(result);
                setTotalEntries(result.length);
                setTotalPages(Math.ceil(result.length / limit));
            }
        } catch (error) {
            console.error('Fetch Error:', error);
            if (error.status !== 403) {
                toast.error('Error connecting to server');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchInvoiceTypes(search, page, pageSize);
        }, 200);

        return () => clearTimeout(delayDebounceFn);
    }, [search, page, pageSize]);

    if (loading) {
        return <PageSkeleton />;
    }

    const columns = [
        ...(isSuperAdmin ? [{ key: 'companyName', label: 'COMPANY', render: (item) => item.Company?.company_name || item.companyId?.companyName || item.companyName || item.company_name || '-' }] : []),
        { key: 'invoiceTypeName', label: 'INVOICE TYPE' },
        {
            key: 'actions',
            label: 'ACTIONS',
            render: (r) => (
                <div className="list-page-action-icons">
                    <button
                        type="button"
                        className="list-page-icon-btn"
                        title="Edit"
                        onClick={() => onEdit(r)}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                    </button>
                    <button
                        type="button"
                        className="list-page-icon-btn"
                        title="Delete"
                        onClick={() => onDelete(r._id || r.id)}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="list-page-container">
            <div className="list-page-header">
                <h1 className="list-page-title">Invoice Type List</h1>
                <div className="list-page-action-buttons">
                    <button className="list-page-btn list-page-btn-add" onClick={onAdd}>
                        ADD INVOICE TYPE
                    </button>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={rows}
                loading={loading}
                emptyMessage="No records found"
                page={page}
                pageSize={pageSize}
                totalEntries={totalEntries}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                searchBar={
                    <SearchBar
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                        placeholder="Search names..."
                    />
                }
            />
        </div>
    );
}

export default InvoiceTypeList;
